import { AppToastOutlet, useAppToastRef } from '../../providers/app-toast-provider';
import '../../assets/entity-list.css';
import { useEffect, useState, useMemo, useRef, useContext } from "react";
import { DataTable, DataTableFilterMeta, DataTableSelectionMultipleChangeEvent } from "primereact/datatable";
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Column } from "primereact/column";
import { ExplainedButton as Button } from '../explained-button';
import { Dialog } from '../dialogs/dialog';
import { GetModuleUsersApiResponseDto, MessageDto, ModuleDto, ModuleUserDto } from "../../general/dtos";
import { ModuleManagementDataContext, ModuleManagementDataContextValue } from '../../providers/module-management-provider';

const emptyAssignments: ModuleUserDto[] = [];
const createFilters = (): DataTableFilterMeta => ({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ...Object.fromEntries(['name', 'email', 'assigned_at'].map(field => [field, {
        operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    }])),
});

interface ModuleAssignmentsProps {
    saveModuleUsers: (module_key: string, assigned: string[]) => Promise<GetModuleUsersApiResponseDto | MessageDto | null>,
}

export function ModuleAssignments({
    saveModuleUsers,
}: ModuleAssignmentsProps) {
  const ctx: ModuleManagementDataContextValue | null = useContext<ModuleManagementDataContextValue | null>(ModuleManagementDataContext);
  const { mode, allUsers, allUsersLoading, moduleUsers, moduleUsersError, moduleUsersLoading, selectedModules } = ctx!;

  const currentModule: ModuleDto | null = selectedModules.length === 1 ? selectedModules[0] : null;
  const moduleKey = currentModule?.module_key ?? '';
  const assigned = moduleUsers[moduleKey] ?? emptyAssignments;
  const [selected, setSelected] = useState<ModuleUserDto[]>([]);
  const [filters, setFilters] = useState<DataTableFilterMeta>(createFilters);
  const [search, setSearch] = useState('');
  const [visibleRows, setVisibleRows] = useState<ModuleUserDto[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const currentKey = useRef(moduleKey); currentKey.current = moduleKey;
  const savePending = useRef(false);
  const [diffVisible, setDiffVisible] = useState(false);
  const toastRef = useAppToastRef();

  useEffect(() => {
    setSelected(assigned); setSaveError('');
  }, [moduleKey, assigned]);

  const rows = useMemo(() => {
    const assignments = new Map(assigned.map(item => [item.sub, item]));
    return allUsers.map(user => ({
      sub: user.sub, picture: user.picture,
      name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email,
      email: user.email, assigned_at: assignments.get(user.sub)?.assigned_at ?? '',
    }));
  }, [allUsers, assigned]);
  const visible = visibleRows ?? rows;
  const updateVisible = (action: 'all' | 'none' | 'invert') => setSelected(previous => {
    const next = new Map(previous.map(row => [row.sub, row]));
    for (const row of visible) {
      if (action === 'none' || (action === 'invert' && next.has(row.sub))) next.delete(row.sub);
      else next.set(row.sub, row);
    }
    return [...next.values()];
  });

  // Diff calculation
  const diff = useMemo(() => {
    const selectedKeys = selected.map((s) => s.sub);
    const assignedKeys = assigned.map((a) => a.sub);

    const added = [...selectedKeys].filter((k) => !assignedKeys.find(l => k === l));
    const removed = [...assignedKeys].filter((k) => !selectedKeys.find(l => k === l));

    return { added, removed };
  }, [selected, assigned]);

  const handleSelectionChange = (event: DataTableSelectionMultipleChangeEvent<ModuleUserDto[]>) => {
    if (saving) return;
    const visibleKeys = new Set(visible.map(row => row.sub));
    setSelected(previous => [...previous.filter(row => !visibleKeys.has(row.sub)), ...(event.value ?? []).filter(row => visibleKeys.has(row.sub))]);
  };

  const handleApply = async () => {
    if (!currentModule || savePending.current) return;
    const key = moduleKey;
    savePending.current = true; setSaving(true); setSaveError('');
    try {
      const result = await saveModuleUsers(key, selected.map(row => row.sub));
      if (currentKey.current !== key) return;
      if (result && 'data' in result) {
        setSelected(result.data);
        toastRef.current?.show({ severity: 'success', summary: 'Assignments updated', detail: 'Module assignments have been saved', life: 2500 });
      } else setSaveError(result && 'message' in result ? result.message : 'Could not save assignments. Your selections are unchanged.');
    } catch { if (currentKey.current === key) setSaveError('Could not save assignments. Your selections are unchanged.'); }
    finally { savePending.current = false; setSaving(false); }
  };

  if (mode === 'create') {
    return <div style={{ padding: 16 }}>Module has not been saved yet, please save it before assigning users</div>;
  }

  if (selectedModules.length === 0) {
    return <div style={{ padding: 16 }}>No module selected</div>;
  }

  if (selectedModules.length > 1) {
    return <div style={{ padding: 16 }}>Multiple modules selected</div>;
  }

  const delta = diff;
  const hasChanges: boolean = delta.added.length > 0 || delta.removed.length > 0;
  const hasError: boolean = moduleUsersError?.length! > 0;
  const canApply: boolean = currentModule !== null && !hasError && hasChanges;

  if (hasError) {
    return <div style={{ padding: 16 }}>The assignments list is unavailable. Please try again.</div>;
  }

  const isLoading = saving || moduleUsersLoading || allUsersLoading || !moduleUsers[moduleKey];
  const busyReason = saving ? 'Wait for the assignment changes to finish saving.' : 'Wait for the users and assignments to finish loading.';

  return (
    <div className="entity-list-container">
        <AppToastOutlet toastRef={toastRef} />
        <div className="entity-list-toolbar admin-toolbar">
            <div className="selection-controls">
                <div className="flex align-items-center gap-2 mb-2 flex-shrink-0">
                    <Button label="All visible" title="Select users matching the current filters" onClick={() => updateVisible('all')} className="p-button-text p-button-sm" disabled={isLoading} disabledReason={busyReason}/>
                    <Button label="None visible" title="Clear selection only for users matching the current filters" onClick={() => updateVisible('none')} className="p-button-text p-button-sm" disabled={isLoading} disabledReason={busyReason}/>
                    <Button label="Invert visible" title="Invert selection only for users matching the current filters" onClick={() => updateVisible('invert')} className="p-button-text p-button-sm" disabled={isLoading} disabledReason={busyReason}/>
                    <Button
                        label="View changes"
                        icon="pi pi-list"
                        severity="secondary"
                        onClick={() => setDiffVisible(true)}
                        visible={hasChanges}
                        disabled={isLoading} disabledReason={busyReason}
                        className="p-button-text p-button-sm"
                    />
                </div>
            </div>
        </div>

        <div className="admin-toolbar mb-2">
            <InputText aria-label="Filter users by name or email" placeholder="Filter users by name or email" value={search}
                disabled={saving} style={{ width: '18rem', maxWidth: '100%' }} onChange={event => {
                    setSearch(event.target.value); setFilters(previous => ({ ...previous, global: { value: event.target.value || null, matchMode: FilterMatchMode.CONTAINS } }));
                }} />
            <Button label="Clear filters" text disabled={saving} disabledReason={busyReason} onClick={() => { setSearch(''); setFilters(createFilters()); }} />
            <small>{selected.length} selected · {visible.length} shown</small>
        </div>
        {saveError && <p role="alert">{saveError}</p>}
        <div className="entity-list-wrapper">
            <DataTable
                value={rows}
                dataKey="sub"
                filters={filters} onFilter={event => setFilters(event.filters)} filterDisplay="menu" globalFilterFields={['name', 'email']}
                sortField="name" sortOrder={1}
                onValueChange={value => setVisibleRows(previous => previous?.length === value.length && previous.every((row, index) => row === value[index]) ? previous : value)}
                selectAll={visible.length > 0 && visible.every(row => selected.some(item => item.sub === row.sub))}
                onSelectAllChange={event => { if (!isLoading) updateVisible(event.checked ? 'all' : 'none'); }}
                selection={selected}
                onSelectionChange={(e: any) => {
                    handleSelectionChange(e);   
                }}
                loading={isLoading}

                selectionMode="checkbox"
                cellSelection={false}

                style={{ height: "100%" }}
                scrollable
                scrollHeight="flex"
                >
                <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                <Column header="" style={{ width: "3rem" }}
                    body={(row) => (
                        row.picture ? (
                            <img
                                src={row.picture}
                                alt={row.name}
                                title={row.name}
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    display: "block",
                                    margin: "0 auto"
                                }}
                                onError={(e) => { e.currentTarget.src = ""; }} // triggers fallback
                            />
                        ) : (
                            <div
                                title={row.name}
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ccc",
                                    color: "#333",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    margin: "0 auto"
                                }}
                            >
                            </div>
                        )
                    )}
                />
                <Column field="name" header="Name" sortable filter showFilterMatchModes={false} filterPlaceholder="Filter name" />
                <Column field="email" header="Email" sortable filter showFilterMatchModes={false} filterPlaceholder="Filter email" />
                <Column field="assigned_at" header="Assigned at" sortable filter showFilterMatchModes={false} filterPlaceholder="YYYY-MM-DD" />
            </DataTable>
        </div>

        <div className="admin-toolbar flex justify-content-end mt-3 flex-shrink-0">
            <Button
            label="Apply"
            icon="pi pi-check"
            onClick={handleApply}
            disabled={isLoading || !canApply}
            disabledReason={isLoading ? busyReason : 'Change an assignment checkbox first. There are no pending assignment changes.'}
            />
        </div>

        {/* Diff Viewer */}
        <Dialog header="Changes preview" visible={diffVisible} onHide={() => setDiffVisible(false)} style={{ width: "450px" }} modal>
            <div className="flex flex-column gap-3">
            <div>
                <h4 className="m-0">Added</h4>
                {diff.added.length === 0 && <p className="m-0 text-600">None</p>}
                {diff.added.map((key) => {
                const mod = allUsers.find((m) => m.sub === key);
                const name: string = ((mod?.first_name ?? '') + ' ' + (mod?.last_name ?? '')).trim();
                return <p key={key} className="m-0 text-green-600">+ {name.length === 0 ? mod?.email : name} ({mod?.email})</p>;
                })}
            </div>

            <div>
                <h4 className="m-0">Removed</h4>
                {diff.removed.length === 0 && <p className="m-0 text-600">None</p>}
                {diff.removed.map((key) => {
                const mod = allUsers.find((m) => m.sub === key);
                const name: string = ((mod?.first_name ?? '') + ' ' + (mod?.last_name ?? '')).trim();
                return <p key={key} className="m-0 text-red-600">– {name.length === 0 ? mod?.email : name} ({mod?.email})</p>;
                })}
            </div>
            </div>
        </Dialog>
    </div>
  );
}
